// Copy for /jumpstart-mw-trial, verbatim from Martin's HTML prototype (jumpstart-mw.html, 29 October 2026): the
// whole-business Jumpstart page sold as a 7-day free trial, beside the customer acquisition and raising investment
// trial pages. What every trial page says is in trial.js; the lines the trial pages share keep the wording the
// 26 October corrections settled on (7-day free trial, "Free" on day one).
import * as shared from './shared'
import * as trial from './trial'

const META = {
  title: 'Jumpstart | Solve the Problem Holding You Back in 30 Days | 7-Day Free Trial',
  description:
    'Find out in about 20 minutes what is really holding your business back, follow a step-by-step plan built for where you are today, and finish day 30 with Martin Warner’s feedback and a written plan.',
}

const hero = {
  ...trial.hero,
  eyebrow: 'Jumpstart · 30-day programme',
  headline: 'Stop guessing alone.',
  headlineAccent: 'Solve the problem holding you back and grow in 30 days.',
  // bold runs are marked **like this**
  checks: [
    '**Find out in about 20 minutes what’s really holding your business back**, and **what to fix first**, so you stop spending time and money on the wrong things',
    '**Follow a step-by-step plan built for where you are today**, whether you’re still an idea or already have your first customers, with a clear path into **planning your business or raising money** when you’re ready',
    '**Never be stuck on your own.** Ask Martin anything, any time, join **two live Q&As**, and learn from **four short weekly deep dive sessions**',
    '**Finish day 30 knowing exactly what to do next**, with **Martin’s feedback and a written plan** you can act on straight away',
  ],
}

// Jack Valentine's result, on its own card under the press strip
const proof = {
  big: '10×',
  text: 'Jack Valentine grew his sales ten-fold.',
  sub: 'Real results from entrepreneurs Martin has mentored.',
}

// the prototype's nine areas, their example scores, the stages by total and the advice for the weakest area
const scorer = {
  ...trial.scorer,
  headline: 'Where exactly is your business',
  areas: [
    'Idea & validation',
    'Product & solution',
    'Customers & sales',
    'Marketing & growth',
    'Money & business model',
    'Funding & investment',
    'Team & hiring',
    'Planning & strategy',
    'Mindset & decisions',
  ],
  start: [3, 2, 2, 3, 3, 1, 3, 3, 4],
  stages: [
    {
      name: 'Exploring',
      min: 9,
      intro:
        'You are at the start, which is the cheapest place to learn what really works. Everything you find out now shapes what you build next.',
      bullets: [
        'Find out who actually needs what you do before you spend on anything',
        'Talk to five people who fit your best-customer description',
        'Jumpstart starts right here, with where you are today',
      ],
    },
    {
      name: 'Starting',
      min: 16,
      intro:
        'You have the beginnings of a business, but not yet a system. Some things are working and you are not sure which.',
      bullets: [
        'Write down what is working, and what is not',
        'Pick one priority and leave the rest for later',
        'Your biggest gain will come from fixing a single bottleneck',
      ],
    },
    {
      name: 'Launched',
      min: 23,
      intro:
        'You have real foundations in place. The work now is turning what happens sometimes into something you can predict.',
      bullets: [
        'Write down what works so it can be repeated, not just remembered',
        'Start tracking the numbers that matter every week',
        'Choose the one area where help would move you furthest',
      ],
    },
    {
      name: 'Growing',
      min: 30,
      intro:
        'The business is growing. The opportunity now is to grow faster without everything depending on you personally.',
      bullets: [
        'Your weak spots are now specific, and fixable',
        'Put your best-performing area under clear ownership',
        'Build the systems and team that work while you are busy',
      ],
    },
    {
      name: 'Scaling',
      min: 38,
      intro:
        'You feel confident across the board. Your question now is how far, and how fast, you can go without breaking what already works.',
      bullets: [
        'Protect what already pays back',
        'Add people, channels and funding in small, measured steps',
        'Decide where deeper support, such as Accelerate, would speed you up',
      ],
    },
  ],
  bottlenecks: [
    {
      intro:
        'You are not yet sure that enough people want what you are building, and that is the riskiest thing to leave untested.',
      bullets: [
        'Write the problem you solve in one sentence',
        'Talk to five people who have that problem before you build anything more',
        'Note what they say in their own words, and what they already do about it',
      ],
    },
    {
      intro: 'What you offer may not yet be clear or different enough for the right people to choose it.',
      bullets: [
        'Describe your product in one sentence a customer would repeat to a friend',
        'Name the result people get, not just the features you provide',
        'Test it with five real people and note exactly where they hesitate',
      ],
    },
    {
      intro:
        'Customers are the heartbeat of a business, and right now there are not enough of them, or the path to the next ones is unclear.',
      bullets: [
        'List every step from first contact to payment and mark where people drop out',
        'Ask three people who did not buy what stopped them',
        'Fix the biggest drop-out point before you add more leads',
      ],
    },
    {
      intro: 'Your customers exist, but you have not found a reliable way to put what you offer in front of them.',
      bullets: [
        'List the places your best customers already spend their time and attention',
        'Pick one channel and run it properly for 30 days instead of spreading across five',
        'Track what each channel costs you in time and money per customer won',
      ],
    },
    {
      intro: 'It is not yet clear how the business makes money, or whether the numbers still work as it grows.',
      bullets: [
        'Write down what it costs you to win a customer and what a customer is worth',
        'Build a simple 12-month view of money in and money out, with your assumptions written down',
        'Choose the Business Planning challenge in weeks 2 to 4 to build a plan around your numbers',
      ],
    },
    {
      intro:
        'You may need outside money to grow, but it is not clear how investors see your business or what would make them say yes.',
      bullets: [
        'Write your story in three sentences: the problem, your solution and why now',
        'List the proof you already have that people want what you are building',
        'Choose the Raising Investment challenge in weeks 2 to 4 to find and fix what is holding your raise back',
      ],
    },
    {
      intro:
        'Businesses are built by people, and right now the team, or the help around you, is not carrying enough of the load.',
      bullets: [
        'Write down what each person does that really matters for the business',
        'Name the gaps honestly and how you plan to fill them',
        'Decide the one hire, partner or adviser that would make the biggest difference',
      ],
    },
    {
      intro:
        'You do not yet have a clear plan for the next 90 days and why. Without one, it is hard to know what is working.',
      bullets: [
        'Set one goal for the next 90 days that you would be proud to hit',
        'Work back from that goal to the three or four things that will make it happen',
        'Review your progress every week and keep a short written note of what you learn',
      ],
    },
    {
      intro:
        'Doubt, overwhelm or second-guessing is slowing your decisions, and that can cost as much as any missing resource.',
      bullets: [
        'Write down the three decisions you have been putting off',
        'Decide each one with a simple rule: what would I do if I had to decide today?',
        'Share one decision with someone who will hold you to it',
      ],
    },
  ],
}

const transformation = {
  ...trial.transformation,
  headline: 'What will change',
  headlineAccent: 'in 30 days?',
  today: {
    ...trial.transformation.today,
    items: [
      'Building before anyone commits to pay',
      'Savings spent on the wrong priority',
      'Confidence worn down by second-guessing',
      'No written plan',
    ],
  },
  day30: {
    ...trial.transformation.day30,
    items: ['One clear priority', 'A challenge done on your business', 'Martin’s feedback', 'A written plan'],
  },
  closer: 'It’s not an information problem. ',
  closerAccent: 'It’s a decision problem.',
  // "Your Jumpstart": the one card, with the visitor's price between the two halves of its kicker
  after: {
    ...trial.transformation.after,
    eyebrow: 'Your Jumpstart',
    headline: 'What do you get',
    headlineAccent: 'in 30 days?',
    lede: 'One month, one price, with Martin alongside you the whole way.',
    programmes: [
      {
        ...trial.transformation.after.jumpstart,
        kicker: ['Starts here', '30 days'],
        items: [
          'An initial assessment reviewed by Martin',
          'A personalised challenge matched to your stage',
          'Live mentoring Q&As and four weekly Deep Dive Clinics',
          '24/7 mentoring and daily insights',
          'An end-of-programme review, in writing',
          '**Solve the problem holding you back**',
        ],
      },
    ],
  },
}

const journey = {
  ...shared.journey,
  headline: 'How does Jumpstart',
  headlineAccent: 'fix it in 30 days?',
  steps: [
    { ...shared.journey.steps[0], text: 'Your Initial Assessment shows exactly where your business stands.' },
    {
      ...shared.journey.steps[1],
      text: 'Start your deeper challenge: Business Planning or Raising Investment, whichever fits you.',
    },
    {
      ...shared.journey.steps[2],
      text: 'Your improvement assessment, and the decisions that matter for your next step.',
    },
    shared.journey.steps[3],
  ],
  // the month's five parts, where the focus pages list their three templates
  templates: {
    title: 'Your month at a glance',
    intro:
      'Allow around 2 to 3 hours a week. It fits around a job or a side business, and each part gives you one clear output.',
    steps: [
      {
        title: 'Two assessments',
        text: 'Your initial assessment on day 1, then an improvement assessment, both reviewed by Martin. ',
        output: 'Output: where you stand, and how far you have moved.',
      },
      {
        title: 'A personalised challenge',
        text: 'Matched to your stage in week 1, then a deeper challenge in weeks 2 to 4: Business Planning or Raising Investment. ',
        output: 'Output: a clear focus.',
      },
      {
        title: 'Core learning module',
        text: 'Identifying Entrepreneurship Opportunities, open from day one. ',
        output: 'Output: sharper decisions.',
      },
      {
        title: 'Live help from Martin',
        text: 'Two live Q&As (recorded), four weekly Deep Dive Clinics, a 24/7 mentoring portal and daily actionable insights. ',
        output: 'Output: answers when you need them.',
      },
      {
        title: 'Martin’s review',
        text: 'Your score, feedback and next step, in writing. ',
        output: 'Output: your next move.',
      },
    ],
    note: 'Still at the idea stage? Every step has a path for you: five conversations instead of past results.',
  },
}

const advice = {
  ...trial.advice,
  quoteStart: '“Take Jumpstart with me, and in 30 days you’ll know exactly what to do next ',
  quoteEnd: 'in your business.”',
}

const compare = {
  ...trial.compare,
  lede: 'Most business learning is either affordable but generic, or personal but expensive. Jumpstart gives you both: coaching-level attention at a course-library price.',
  table: {
    ...trial.compare.table,
    rows: [
      { label: 'Price', cells: ['{price} once', 'Monthly fee', 'One-off purchase', '$150–$600 an hour'] },
      {
        label: 'Starts with your situation',
        cells: [{ yes: 'Yes' }, { no: 'No' }, { no: 'No' }, { yes: 'Yes' }],
      },
      {
        label: 'A founder reviews your work',
        cells: [{ yes: 'Martin' }, { no: 'No' }, { no: 'No' }, 'Depends on the coach'],
      },
      {
        label: 'Live access to founder mentoring',
        cells: [
          [
            { yes: '2 live mentoring Q&As' },
            { yes: '4 deep dive sessions on topics every entrepreneur needs' },
            { yes: '24/7 Mentoring' },
          ],
          { no: 'No' },
          { no: 'No' },
          { yes: 'By the hour' },
        ],
      },
      {
        label: 'You finish with',
        cells: [
          'A written review, next step & plan from Martin',
          'Course completion',
          'Videos watched',
          'Notes from your sessions',
        ],
      },
    ],
  },
}

// the prototype keeps warpSpeed in Martin's credentials, which the focus pages trimmed
const mentor = {
  ...trial.mentor,
  credentials: [
    ['USA Today best-selling author, ', 'The Startup Story'],
    ['First British entrepreneur to build an electric aviation aircraft'],
    ['Founder and CEO of warpSpeed'],
    ['No. 1 in T3 Magazine’s Top 100 products for his ProDesk3D printer'],
  ],
}

const offer = {
  ...trial.offer,
  stack: [
    {
      title: '1. Know where you are',
      text: 'An initial assessment reviewed by Martin, a personalised challenge matched to your stage (Business Planning or Raising Investment), an improvement assessment and your first core module from the Accelerate programme.',
    },
    {
      title: '2. Your personalised challenge',
      text: 'Matched to your stage in week 1, then a deeper challenge in weeks 2 to 4: Business Planning or Raising Investment. Practical, and built around your own business.',
    },
    {
      title: '3. Live mentoring and Deep Dive Clinics',
      text: 'Two live mentoring Q&As with Martin, and four weekly Deep Dive Clinics, recorded if you can’t attend.',
    },
    {
      title: '4. Help between events',
      text: 'A 24/7 mentoring portal to ask Martin directly, daily actionable insights, and periodic founder research reports such as AI Lookout 2026 and Startup Recruitment 2026.',
    },
    {
      title: '5. Clear next steps',
      text: 'An end-of-programme review: your score, Martin’s feedback and your next step, in writing.',
    },
    shared.offer.stack[5],
  ],
}

const faq = {
  ...trial.faq,
  items: [
    trial.faq.howTrial,
    {
      q: 'Is this for beginners?',
      a: 'Yes. Jumpstart starts with where you are, whether you are still at the idea stage or already growing. Your assessments shape everything after.',
    },
    {
      q: 'What if I don’t have a business idea yet?',
      a: 'That is fine. Your starting point is matched to what you are trying to work out, and every step has a path for you, such as five conversations instead of past results.',
    },
    {
      q: 'How much time does it take?',
      a: 'Allow around 2 to 3 hours a week. It fits around a job or a side business, and your first two assessments take about 20 minutes together.',
    },
    {
      q: 'What if I miss a live session?',
      a: 'The live Q&As and the Deep Dive Clinics are recorded, so you can watch whenever it suits you.',
    },
    trial.faq.martin,
    trial.faq.clinics,
    {
      q: 'Is it a course, or is it live?',
      a: 'Both. You work through your assessments, core module and challenge at your own pace, with live Q&As and mentoring from Martin alongside.',
    },
    {
      q: 'How is it different from an online course?',
      a: 'It starts with your situation, includes live access and mentoring, and ends with a written review from Martin rather than a certificate of completion.',
    },
    {
      q: 'Does it work for a side business?',
      a: 'Yes. You work on your own business, whatever its size, and it fits around your other commitments.',
    },
    trial.faq.afterJumpstart,
    {
      q: 'Does Jumpstart guarantee success?',
      a: 'No, and nobody honestly can. Jumpstart gives you a diagnosis, a plan and Martin’s feedback; results depend on what you do with them. See our Earnings and Income Disclaimer.',
      isNew: true,
    },
  ],
}

const finalCta = {
  ...trial.finalCta,
  headline: 'Stop guessing alone.',
  headlineAccent: 'Know your next move.',
}

const content = {
  key: 'trial',
  trial: trial.trial,
  META,
  trusted: shared.trusted,
  hero,
  video: shared.video,
  proof,
  // the prototype has no "Sound familiar?" and no ladder
  problem: null,
  stage: null,
  scorer,
  transformation,
  journey,
  advice,
  compare,
  mentor,
  memberVideos: trial.memberVideos,
  testimonials: trial.testimonials,
  offer,
  faq,
  finalCta,
}

export default content
